/**
 * The filter row above a Bexio workspace table.
 *
 * Shared by the invoice and contact pages because the two rows are the same
 * shape, not because a generic filter engine seemed nice: each page still
 * declares its own controls and owns its own state. This component only lays
 * them out and renders the reset affordance.
 *
 * HONESTY ABOUT SCOPE is the reason `searchHint` exists. Both list endpoints now
 * search server-side, but over a NAMED set of columns — invoices over document
 * number and title, contacts over number, name and mail. A box labelled only
 * "suchen" implies it searches everything shown, and on the invoice page that
 * would be false (the contact column is filled after the query and is not
 * searchable). The hint states the scope where it is narrower than the table.
 */
import React from "react";
import { RotateCcw, Search } from "lucide-react";

export default function BexioFilterBar({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Suchen …",
  searchHint,
  searchLabel = "Suche",
  children,
  onReset,
  canReset,
  resultLabel,
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 basis-56">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            aria-label={searchLabel}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full rounded-lg border border-border bg-background py-2 pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>

        {children}

        {onReset ? (
          <button
            type="button"
            onClick={onReset}
            disabled={!canReset}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RotateCcw aria-hidden="true" className="h-3.5 w-3.5" />
            Filter zurücksetzen
          </button>
        ) : null}

        {resultLabel ? (
          <span className="ml-auto shrink-0 text-xs text-muted-foreground">
            {resultLabel}
          </span>
        ) : null}
      </div>

      {searchHint ? (
        <p className="mt-2 text-xs text-muted-foreground">{searchHint}</p>
      ) : null}
    </div>
  );
}

/** A labelled select, styled to match the search input beside it. */
export function BexioFilterSelect({ label, value, onChange, options }) {
  return (
    <label className="inline-flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
      <span className="sr-only sm:not-sr-only">{label}</span>
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border border-border bg-background px-2.5 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
